import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Car,
  Building2,
  Bed,
  Calendar as CalendarIcon,
  Clock,
  User,
  FileText,
  CreditCard,
  Loader2,
  Plus,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Receipt,
  ChevronRight,
  MapPin,
  Users,
  Info,
} from 'lucide-react';
import { format, addDays, differenceInDays } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResidentBookings() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [facilities, setFacilities] = useState([]);
  const [parkingBays, setParkingBays] = useState([]);
  const [venues, setVenues] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [calendarBookings, setCalendarBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('facilities');
  
  // Booking dialogs
  const [showParkingDialog, setShowParkingDialog] = useState(false);
  const [showVenueDialog, setShowVenueDialog] = useState(false);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Parking form
  const [parkingForm, setParkingForm] = useState({
    parking_bay: '',
    start_date: null,
    end_date: null,
    vehicle_registration: '',
    visitor_name: '',
    notes: ''
  });
  
  // Venue form
  const [venueForm, setVenueForm] = useState({
    venue_name: '',
    start_date: null,
    end_date: null,
    purpose: '',
    expected_guests: '',
    special_requirements: '',
    notes: ''
  });

  const hoaId = user?.hoa_id || 'demo_hoa';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [facilitiesRes, bookingsRes, calendarRes] = await Promise.all([
        fetch(`${API_URL}/api/bookings/facilities/${hoaId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/bookings/my-bookings`, { credentials: 'include' }),
        fetch(`${API_URL}/api/bookings/calendar/${hoaId}`, { credentials: 'include' })
      ]);

      if (facilitiesRes.ok) {
        const data = await facilitiesRes.json();
        setFacilities(data.facilities || []);
        setParkingBays(data.parking_bays || []);
        setVenues(data.venues || []);
      }

      if (bookingsRes.ok) {
        setMyBookings(await bookingsRes.json());
      }

      if (calendarRes.ok) {
        setCalendarBookings(await calendarRes.json());
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load booking data');
    } finally {
      setLoading(false);
    }
  };

  const handleParkingSubmit = async () => {
    if (!parkingForm.parking_bay || !parkingForm.start_date || !parkingForm.end_date || !parkingForm.vehicle_registration) {
      toast.error('Please fill in all required fields');
      return;
    }

    const nights = differenceInDays(parkingForm.end_date, parkingForm.start_date);
    if (nights < 1) {
      toast.error('Minimum booking duration is 1 night');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/bookings/visitor-parking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          facility_type: 'visitor_parking',
          parking_bay: parkingForm.parking_bay,
          start_date: parkingForm.start_date.toISOString(),
          end_date: parkingForm.end_date.toISOString(),
          number_of_nights: nights,
          vehicle_registration: parkingForm.vehicle_registration,
          visitor_name: parkingForm.visitor_name,
          notes: hoaId
        })
      });

      if (response.ok) {
        toast.success('Booking request submitted! Awaiting agent approval.');
        setShowParkingDialog(false);
        setParkingForm({ parking_bay: '', start_date: null, end_date: null, vehicle_registration: '', visitor_name: '', notes: '' });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit booking');
      }
    } catch (error) {
      toast.error('Failed to submit booking');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVenueSubmit = async () => {
    if (!venueForm.venue_name || !venueForm.start_date || !venueForm.end_date || !venueForm.purpose) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/bookings/venue-hire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          facility_type: 'venue_hire',
          venue_name: venueForm.venue_name,
          start_date: venueForm.start_date.toISOString(),
          end_date: venueForm.end_date.toISOString(),
          purpose: venueForm.purpose,
          expected_guests: venueForm.expected_guests ? parseInt(venueForm.expected_guests) : null,
          special_requirements: venueForm.special_requirements,
          notes: hoaId
        })
      });

      if (response.ok) {
        toast.success('Venue booking request submitted!');
        setShowVenueDialog(false);
        setVenueForm({ venue_name: '', start_date: null, end_date: null, purpose: '', expected_guests: '', special_requirements: '', notes: '' });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to submit booking');
      }
    } catch (error) {
      toast.error('Failed to submit booking');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;

    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Booking cancelled');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to cancel');
      }
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/bookings/pay/${invoiceId}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Payment successful! Your booking is now confirmed.');
        setShowInvoiceDialog(false);
        setSelectedInvoice(null);
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Payment failed');
      }
    } catch (error) {
      toast.error('Payment failed');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: <Badge className="bg-amber-100 text-amber-700"><AlertCircle className="w-3 h-3 mr-1" />Pending Approval</Badge>,
      invoice_issued: <Badge className="bg-blue-100 text-blue-700"><Receipt className="w-3 h-3 mr-1" />Invoice Issued</Badge>,
      paid: <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Paid</Badge>,
      confirmed: <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Confirmed</Badge>,
      cancelled: <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 mr-1" />Cancelled</Badge>,
    };
    return badges[status] || <Badge variant="secondary">{status}</Badge>;
  };

  const getFacilityIcon = (type) => {
    const icons = {
      visitor_parking: <Car className="w-6 h-6" />,
      venue_hire: <Building2 className="w-6 h-6" />,
      guest_facility: <Bed className="w-6 h-6" />,
    };
    return icons[type] || <Building2 className="w-6 h-6" />;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-ZA', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check if a parking bay is available for selected dates
  const isParkingBayAvailable = (bayId) => {
    if (!parkingForm.start_date || !parkingForm.end_date) return true;
    
    return !calendarBookings.some(booking => 
      booking.parking_bay === bayId &&
      booking.status !== 'cancelled' &&
      new Date(booking.start_date) < parkingForm.end_date &&
      new Date(booking.end_date) > parkingForm.start_date
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="resident-bookings-page">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold text-slate-900">Bookings</h1>
            <p className="text-xs text-slate-500">Reserve facilities & parking</p>
          </div>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6 space-y-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 bg-white border">
            <TabsTrigger value="facilities" className="data-[state=active]:bg-purple-50 data-[state=active]:text-purple-700">
              <Building2 className="w-4 h-4 mr-2" />
              Book Facilities
            </TabsTrigger>
            <TabsTrigger value="my-bookings" className="data-[state=active]:bg-purple-50 data-[state=active]:text-purple-700">
              <CalendarIcon className="w-4 h-4 mr-2" />
              My Bookings
            </TabsTrigger>
          </TabsList>

          {/* Facilities Tab */}
          <TabsContent value="facilities" className="mt-4 space-y-4">
            {facilities.map(facility => (
              <Card 
                key={facility.id} 
                className={`cursor-pointer transition-all hover:shadow-md ${facility.coming_soon ? 'opacity-60' : ''}`}
                onClick={() => {
                  if (facility.coming_soon) {
                    toast.info('Coming soon!');
                    return;
                  }
                  if (facility.id === 'visitor_parking') setShowParkingDialog(true);
                  else if (facility.id === 'venue_hire') setShowVenueDialog(true);
                }}
                data-testid={`facility-${facility.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                      facility.id === 'visitor_parking' ? 'bg-blue-100 text-blue-600' :
                      facility.id === 'venue_hire' ? 'bg-purple-100 text-purple-600' :
                      'bg-amber-100 text-amber-600'
                    }`}>
                      {getFacilityIcon(facility.id)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{facility.name}</h3>
                        {facility.coming_soon && (
                          <Badge variant="secondary" className="text-xs">Coming Soon</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{facility.description}</p>
                      <p className="text-xs text-purple-600 mt-2">{facility.pricing}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          {/* My Bookings Tab */}
          <TabsContent value="my-bookings" className="mt-4 space-y-4">
            {myBookings.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center">
                  <CalendarIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-700">No Bookings Yet</h3>
                  <p className="text-sm text-slate-500 mt-1">Your bookings will appear here</p>
                  <Button 
                    className="mt-4 bg-purple-600 hover:bg-purple-700"
                    onClick={() => setActiveTab('facilities')}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Make a Booking
                  </Button>
                </CardContent>
              </Card>
            ) : (
              myBookings.map(booking => (
                <Card key={booking.booking_id} data-testid={`booking-${booking.booking_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          booking.facility_type === 'visitor_parking' ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'
                        }`}>
                          {getFacilityIcon(booking.facility_type)}
                        </div>
                        <div>
                          <h4 className="font-medium text-slate-900">
                            {booking.facility_type === 'visitor_parking' 
                              ? `Visitor Parking - ${booking.parking_bay}`
                              : `Venue - ${booking.venue_name}`
                            }
                          </h4>
                          <p className="text-xs text-slate-500">#{booking.booking_id.slice(-8)}</p>
                        </div>
                      </div>
                      {getStatusBadge(booking.status)}
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-slate-600">
                        <CalendarIcon className="w-4 h-4" />
                        <span>{formatDate(booking.start_date)} - {formatDate(booking.end_date)}</span>
                      </div>
                      {booking.vehicle_registration && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Car className="w-4 h-4" />
                          <span>Vehicle: {booking.vehicle_registration}</span>
                        </div>
                      )}
                      {booking.visitor_name && (
                        <div className="flex items-center gap-2 text-slate-600">
                          <User className="w-4 h-4" />
                          <span>Visitor: {booking.visitor_name}</span>
                        </div>
                      )}
                    </div>

                    {/* Invoice Section */}
                    {booking.invoice && (
                      <div className="mt-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-slate-700">Invoice</p>
                            <p className="text-lg font-bold text-slate-900">R{booking.invoice.amount?.toFixed(2)}</p>
                            <p className="text-xs text-slate-500">{booking.invoice.description}</p>
                          </div>
                          {booking.invoice.status === 'pending' && (
                            <Button 
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => {
                                setSelectedInvoice(booking.invoice);
                                setShowInvoiceDialog(true);
                              }}
                            >
                              <CreditCard className="w-4 h-4 mr-1" />
                              Pay Now
                            </Button>
                          )}
                          {booking.invoice.status === 'paid' && (
                            <Badge className="bg-emerald-100 text-emerald-700">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Paid
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    {['pending', 'invoice_issued'].includes(booking.status) && (
                      <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleCancelBooking(booking.booking_id)}
                        >
                          <XCircle className="w-4 h-4 mr-1" />
                          Cancel Booking
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Visitor Parking Booking Dialog */}
      <Dialog open={showParkingDialog} onOpenChange={setShowParkingDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="w-5 h-5 text-blue-600" />
              Book Overnight Visitor Parking
            </DialogTitle>
            <DialogDescription>
              Reserve a parking bay for your visitors
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Auto-populated resident info */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600"><strong>Resident:</strong> {user?.name || 'Resident'}</p>
              <p className="text-sm text-slate-600"><strong>Unit:</strong> {user?.unit_number || 'N/A'}</p>
            </div>

            {/* Parking Bay Selection */}
            <div>
              <Label>Select Parking Bay *</Label>
              <Select
                value={parkingForm.parking_bay}
                onValueChange={(v) => setParkingForm({ ...parkingForm, parking_bay: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a parking bay..." />
                </SelectTrigger>
                <SelectContent>
                  {parkingBays.map(bay => {
                    const available = isParkingBayAvailable(bay.bay_id);
                    return (
                      <SelectItem 
                        key={bay.bay_id} 
                        value={bay.bay_id}
                        disabled={!available}
                      >
                        {bay.name || bay.bay_id} {!available && '(Booked)'}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Date Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="datetime-local"
                  value={parkingForm.start_date ? format(parkingForm.start_date, "yyyy-MM-dd'T'HH:mm") : ''}
                  onChange={(e) => setParkingForm({ 
                    ...parkingForm, 
                    start_date: e.target.value ? new Date(e.target.value) : null 
                  })}
                  min={format(new Date(), "yyyy-MM-dd'T'HH:mm")}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>End Date *</Label>
                <Input
                  type="datetime-local"
                  value={parkingForm.end_date ? format(parkingForm.end_date, "yyyy-MM-dd'T'HH:mm") : ''}
                  onChange={(e) => setParkingForm({ 
                    ...parkingForm, 
                    end_date: e.target.value ? new Date(e.target.value) : null 
                  })}
                  min={parkingForm.start_date ? format(addDays(parkingForm.start_date, 1), "yyyy-MM-dd'T'HH:mm") : ''}
                  className="mt-1"
                />
              </div>
            </div>

            {parkingForm.start_date && parkingForm.end_date && (
              <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 p-2 rounded">
                <Info className="w-4 h-4" />
                <span>
                  {differenceInDays(parkingForm.end_date, parkingForm.start_date)} night(s) selected
                </span>
              </div>
            )}

            {/* Vehicle Registration */}
            <div>
              <Label>Vehicle Registration Number *</Label>
              <Input
                value={parkingForm.vehicle_registration}
                onChange={(e) => setParkingForm({ ...parkingForm, vehicle_registration: e.target.value.toUpperCase() })}
                placeholder="e.g., CA 123 456"
                className="mt-1"
              />
            </div>

            {/* Visitor Name */}
            <div>
              <Label>Visitor Name (Optional)</Label>
              <Input
                value={parkingForm.visitor_name}
                onChange={(e) => setParkingForm({ ...parkingForm, visitor_name: e.target.value })}
                placeholder="Name of visitor"
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowParkingDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleParkingSubmit} 
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Venue Hire Booking Dialog */}
      <Dialog open={showVenueDialog} onOpenChange={setShowVenueDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-purple-600" />
              Book Venue
            </DialogTitle>
            <DialogDescription>
              Reserve a venue for your event
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Auto-populated resident info */}
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600"><strong>Resident:</strong> {user?.name || 'Resident'}</p>
              <p className="text-sm text-slate-600"><strong>Unit:</strong> {user?.unit_number || 'N/A'}</p>
            </div>

            {/* Venue Selection */}
            <div>
              <Label>Select Venue *</Label>
              <Select
                value={venueForm.venue_name}
                onValueChange={(v) => setVenueForm({ ...venueForm, venue_name: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a venue..." />
                </SelectTrigger>
                <SelectContent>
                  {venues.map(venue => (
                    <SelectItem key={venue.venue_id} value={venue.name}>
                      {venue.name} (Capacity: {venue.capacity})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date & Time *</Label>
                <Input
                  type="datetime-local"
                  value={venueForm.start_date ? format(venueForm.start_date, "yyyy-MM-dd'T'HH:mm") : ''}
                  onChange={(e) => setVenueForm({ 
                    ...venueForm, 
                    start_date: e.target.value ? new Date(e.target.value) : null 
                  })}
                  min={format(new Date(), "yyyy-MM-dd'T'HH:mm")}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>End Date & Time *</Label>
                <Input
                  type="datetime-local"
                  value={venueForm.end_date ? format(venueForm.end_date, "yyyy-MM-dd'T'HH:mm") : ''}
                  onChange={(e) => setVenueForm({ 
                    ...venueForm, 
                    end_date: e.target.value ? new Date(e.target.value) : null 
                  })}
                  min={venueForm.start_date ? format(venueForm.start_date, "yyyy-MM-dd'T'HH:mm") : ''}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Purpose */}
            <div>
              <Label>Purpose of Booking *</Label>
              <Input
                value={venueForm.purpose}
                onChange={(e) => setVenueForm({ ...venueForm, purpose: e.target.value })}
                placeholder="e.g., Birthday party, Meeting"
                className="mt-1"
              />
            </div>

            {/* Expected Guests */}
            <div>
              <Label>Expected Number of Guests</Label>
              <Input
                type="number"
                value={venueForm.expected_guests}
                onChange={(e) => setVenueForm({ ...venueForm, expected_guests: e.target.value })}
                placeholder="e.g., 20"
                className="mt-1"
              />
            </div>

            {/* Special Requirements */}
            <div>
              <Label>Special Requirements (Optional)</Label>
              <Textarea
                value={venueForm.special_requirements}
                onChange={(e) => setVenueForm({ ...venueForm, special_requirements: e.target.value })}
                placeholder="Any special setup or requirements..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowVenueDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleVenueSubmit} 
              disabled={submitting}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Booking'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Payment Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-emerald-600" />
              Pay Invoice
            </DialogTitle>
          </DialogHeader>

          {selectedInvoice && (
            <div className="py-4">
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-600">Invoice #:</span>
                  <span className="font-mono text-sm">{selectedInvoice.invoice_id?.slice(-8)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Description:</span>
                  <span className="text-right">{selectedInvoice.description}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Due Date:</span>
                  <span>{new Date(selectedInvoice.due_date).toLocaleDateString()}</span>
                </div>
                <div className="border-t pt-3 flex justify-between items-center">
                  <span className="font-medium text-slate-700">Amount Due:</span>
                  <span className="text-2xl font-bold text-emerald-600">R{selectedInvoice.amount?.toFixed(2)}</span>
                </div>
              </div>

              <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-start gap-2">
                <Info className="w-4 h-4 text-blue-600 mt-0.5" />
                <p className="text-sm text-blue-700">
                  Once payment is completed, your booking will be automatically confirmed.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowInvoiceDialog(false); setSelectedInvoice(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={() => handlePayInvoice(selectedInvoice?.invoice_id)}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  <CreditCard className="w-4 h-4 mr-2" />
                  Pay R{selectedInvoice?.amount?.toFixed(2)}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
